<template>
  <div ref="tourViewerRoot" class="tour-viewer" :class="[currentTheme, { 'light': template.uiMode === 'light' }]">
    <!-- Unpublished Tour Page -->
    <!-- Suspended / Blocked Screen -->
    <div v-if="suspendedData" class="unpublished-screen">
      <div class="unpublished-card">
        <div class="unpublished-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#94a3b8" stroke-width="1.5"/>
            <path d="M12 8v4m0 4h.01" stroke="#94a3b8" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <h1 class="unpublished-title">{{ t('suspended.title') }}</h1>
        <div class="unpublished-badge">
          <span>{{ suspendedData.reason === 'account_blocked' ? t('suspended.accountBlocked') : t('suspended.tourSuspended') }}</span>
        </div>
        <p class="unpublished-hint">{{ t('suspended.contact') }}</p>
        <a href="https://virtualtoureasy.com" class="unpublished-link" target="_blank">
          virtualtoureasy.com
        </a>
      </div>
    </div>

    <div v-if="unpublishedData && !suspendedData" class="unpublished-screen">
      <div class="unpublished-bg" v-if="unpublishedData.cover_image_url">
        <img :src="unpublishedData.cover_image_url" alt="" class="unpublished-bg-img" />
      </div>
      <div class="unpublished-card">
        <div class="unpublished-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#94a3b8" stroke-width="1.5"/>
            <path d="M12 8v4m0 4h.01" stroke="#94a3b8" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <h1 class="unpublished-title">{{ unpublishedData.title }}</h1>
        <div class="unpublished-badge">
          <span v-if="unpublishedData.status === 'draft'">🚧 This tour is not yet published</span>
          <span v-else-if="unpublishedData.status === 'archived'">📦 This tour has been archived</span>
          <span v-else>🔒 This tour is currently unavailable</span>
        </div>
        <p class="unpublished-hint">Please contact the tour owner for access.</p>
        <a href="https://virtualtoureasy.com" class="unpublished-link" target="_blank">
          Powered by VirtualTourEasy
        </a>
      </div>
    </div>

    <!-- Password Gate -->
    <PasswordGate
      v-if="passwordRequired && !unpublishedData"
      ref="passwordGate"
      :tour-title="passwordTourTitle"
      :cover-image="passwordCoverImage"
      @verify="handlePasswordVerify"
    />

    <!-- Tour Preview / Landing Page -->
    <TourPreview
      v-if="showPreview && !passwordRequired && !loading && !error"
      :title="tourTitle"
      :description="tourDescription"
      :cover-image="tourCoverImage"
      :scene-count="scenes.length"
      :started="!showPreview"
      :branding="effectiveBranding"
      @start="startTour"
    />

    <!-- Loading Screen -->
    <div v-if="loading && !passwordRequired" class="loading-screen">
      <div class="loading-content">
        <div class="loading-logo">
          <img :src="vteLogo" alt="VirtualTourEasy" class="vte-loader-logo" />
        </div>
        <div class="loading-spinner"></div>
        <div class="loading-text">{{ loadingText }}</div>
      </div>
    </div>

    <!-- Error Screen -->
    <div v-if="error && !passwordRequired" class="error-screen">
      <div class="error-card">
        <div class="error-icon-wrap">
          <svg v-if="isNotFoundError" width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#64748b" stroke-width="1.5"/>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill="#64748b"/>
          </svg>
          <svg v-else width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#f59e0b" stroke-width="1.5"/>
            <path d="M12 8v4m0 4h.01" stroke="#f59e0b" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <h2 class="error-title">{{ isNotFoundError ? 'Tour Not Available' : 'Something Went Wrong' }}</h2>
        <p class="error-detail">{{ errorMessage }}</p>
        <p class="error-hint">{{ isNotFoundError ? 'This tour may have been removed or the link may be incorrect. Please check the URL or contact the tour owner.' : 'There was a problem loading this tour. Please try again.' }}</p>
        <button v-if="!isNotFoundError" class="error-retry" @click="retryLoad">Try Again</button>
        <a href="https://virtualtoureasy.com" class="error-home" target="_blank">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="vertical-align: middle; margin-right: 4px;">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill="currentColor"/>
          </svg>
          VirtualTourEasy.com
        </a>
      </div>
    </div>

    <!-- Tour Container -->
    <div v-show="!loading && !error" class="tour-container" :class="`layout-${template.sceneListPosition}`">
      <!-- Panorama View -->
      <div ref="panoramaContainer" class="panorama-container"></div>

      <!-- Hotspots -->
      <HotspotRenderer
        v-if="activeScene?.hotspots?.length"
        :hotspots="activeScene.hotspots"
        :camera="threeCamera"
        :container-el="panoramaContainer || null"
        :sphere-radius="500"
        :camera-changed="cameraChanged"
        :vr-mode="vrActive"
        @navigate-scene="handleHotspotNavigate"
      />

      <!-- Scene Title Overlay -->
      <SceneTitle
        v-if="template.showSceneTitle && activeScene"
        :title="activeScene.title"
        :position="template.sceneTitlePosition"
        :show-counter="template.showSceneCounter"
        :current="activeSceneIndex + 1"
        :total="scenes.length"
      />

      <!-- Nav Arrows -->
      <NavArrows
        v-if="template.showNavArrows && scenes.length > 1"
        :total-scenes="scenes.length"
        @prev="prevScene"
        @next="nextScene"
      />

      <!-- Controls (center bar above scenes) -->
      <div v-if="showControls" class="controls-container" :class="[`controls-${template.controlsPosition}`, `controls-${template.controlsStyle}`]">
        <button @click="toggleAutoRotate" :title="autoRotate ? 'Pause' : 'Auto rotate'">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path v-if="autoRotate" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
            <path v-else d="M8 5v14l11-7z"/>
          </svg>
        </button>
        <button @click="recenterView" title="Recenter view">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0013 3.06V1h-2v2.06A8.994 8.994 0 003.06 11H1v2h2.06A8.994 8.994 0 0011 20.94V23h2v-2.06A8.994 8.994 0 0020.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
          </svg>
        </button>
        <button @click="toggleVR" v-if="isVRSupported" :title="t('controls.vrMode')" :class="{ active: gyroscopeActive }" class="vr-btn">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.74 6H3.21C2.55 6 2 6.57 2 7.28v8.44c0 .7.55 1.28 1.23 1.28h4.79c.52 0 .98-.34 1.14-.84l.99-3.11c.23-.71.88-1.19 1.63-1.19h.44c.75 0 1.4.48 1.63 1.19l.99 3.11c.16.5.62.84 1.14.84h4.79c.68 0 1.23-.57 1.23-1.28V7.28C22 6.57 21.42 6 20.74 6zM7.5 14c-1.38 0-2.5-1.12-2.5-2.5S6.12 9 7.5 9 10 10.12 10 11.5 8.88 14 7.5 14zm9 0c-1.38 0-2.5-1.12-2.5-2.5S15.12 9 16.5 9 19 10.12 19 11.5 17.88 14 16.5 14z"/>
          </svg>
        </button>
        <button @click="showTemplateMenu = !showTemplateMenu" title="Change layout" class="template-btn">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
          </svg>
        </button>
      </div>

      <!-- Fullscreen button — only shown when the API is actually supported (hidden on iOS) -->
      <button v-if="isFullscreenSupported" @click="toggleFullscreen" class="fullscreen-toggle" :title="t('common.fullscreen')">
        <svg v-if="!isFullscreen" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
        </svg>
        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
        </svg>
      </button>

      <!-- Template Menu -->
      <div v-if="showTemplateMenu" class="template-menu">
        <div class="template-menu-title">Layout</div>
        <button
          v-for="tmpl in templateList"
          :key="tmpl.id"
          class="template-option"
          :class="{ active: tmpl.id === template.id }"
          @click="selectTemplate(tmpl.id)"
        >
          <span class="template-option-name">{{ tmpl.name }}</span>
          <span class="template-option-desc">{{ tmpl.description }}</span>
        </button>
      </div>

      <!-- Scene List -->
      <SceneList
        ref="sceneListWrapperEl"
        :scenes="scenes"
        :active-scene-id="activeSceneId"
        :position="template.sceneListPosition"
        :list-style="template.sceneListStyle"
        :thumbnail-size="template.thumbnailSize"
        :collapsible="template.sceneListCollapsible"
        :default-collapsed="template.sceneListDefaultCollapsed"
        :show-counter="template.showSceneCounter"
        @select="switchScene"
      />

      <!-- Branding Overlay (custom or default VTE) -->
      <BrandingOverlay :branding="effectiveBranding" />

      <!-- Free Plan Watermark (cannot be removed by branding settings) -->
      <div v-if="showFreeWatermark" class="free-watermark">
        <a href="https://virtualtoureasy.com" target="_blank" rel="noopener">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
          </svg>
          Powered by <strong>VirtualTourEasy</strong>
        </a>
      </div>

      <!-- Zoom Controls -->
      <ZoomControls @zoom-in="zoomIn" @zoom-out="zoomOut" />

      <!-- Contact / Lead Form Button -->
      <button v-if="leadFormEnabled" @click="showLeadForm = true" class="contact-toggle" :title="t('lead.title')">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
        </svg>
      </button>

      <!-- Lead Form Modal -->
      <LeadFormModal
        v-if="leadFormEnabled"
        :open="showLeadForm"
        :tour-uuid="tourId || ''"
        :api-url="props.apiUrl || ''"
        @close="showLeadForm = false"
      />

      <!-- Share Button -->
      <button @click="showSharePanel = true" class="share-toggle" title="Share">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
        </svg>
      </button>

      <!-- Report Button — bottom is dynamic to stay above scene list panel -->
      <button @click="showReportModal = true" class="report-toggle" :style="{ bottom: reportButtonBottom }" :title="t('report.title')">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6h-5.6z"/>
        </svg>
      </button>

      <!-- Report Modal -->
      <Teleport to="body">
        <div v-if="showReportModal" class="report-overlay" @click.self="showReportModal = false">
          <div class="report-modal">
            <button class="report-close" @click="showReportModal = false">×</button>
            <div v-if="reportSubmitted" class="report-success">
              <div class="report-success-icon">✓</div>
              <h3>{{ t('report.thankYou') }}</h3>
              <p>{{ t('report.successMessage') }}</p>
            </div>
            <template v-else>
              <h3 class="report-title">{{ t('report.title') }}</h3>
              <p class="report-subtitle">{{ t('report.subtitle') }}</p>
              <div class="report-reasons">
                <label v-for="reason in reportReasons" :key="reason.key" class="report-reason">
                  <input type="radio" :value="reason.key" v-model="reportReason" />
                  <span>{{ t(reason.label) }}</span>
                </label>
              </div>
              <textarea v-model="reportDetails" :placeholder="t('report.details')" class="report-textarea" rows="3"></textarea>
              <p v-if="reportError" class="report-error">{{ reportError }}</p>
              <button @click="submitReport" class="report-submit" :disabled="!reportReason || reportSubmitting">
                {{ reportSubmitting ? '...' : t('report.submit') }}
              </button>
            </template>
          </div>
        </div>
      </Teleport>

      <!-- Share Panel -->
      <SharePanel
        :open="showSharePanel"
        :tour-title="tourTitle"
        :tour-id="tourId"
        :tracker="analyticsTracker"
        @close="showSharePanel = false"
      />

      <!-- Scene Description -->
      <SceneDescription :description="activeScene?.description" />

      <!-- Tour Map -->
      <TourMap
        :lat="tourLat"
        :lng="tourLng"
        :title="tourTitle"
        :google-maps-api-key="googleMapsApiKey"
      />

      <!-- Floor Plan -->
      <FloorPlan
        v-if="floorPlans.length > 0"
        :floor-plans="floorPlans"
        :active-scene-id="activeSceneId"
        :always-show="layoutAlwaysShow"
        @navigate="switchScene"
      />

      <!-- Audio Mini Player -->
      <div v-if="hasAudio" class="audio-player" :class="{ 'audio-playing': isAudioPlaying }">
        <!-- Animated bars (playing indicator) — hidden on mobile -->
        <div class="audio-bars" :class="{ playing: isAudioPlaying }">
          <span></span><span></span><span></span><span></span>
        </div>
        <!-- Play / Pause -->
        <button class="audio-btn" @click="toggleAudio" :title="isAudioPlaying ? 'Pause' : 'Play'">
          <!-- Music note icon shown when paused -->
          <svg class="audio-icon-note" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 3v10.55A4 4 0 1014 17V7h4V3h-6z"/>
          </svg>
          <!-- Play/pause icon shown when playing -->
          <svg class="audio-icon-play" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path v-if="isAudioPlaying" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
            <path v-else d="M8 5v14l11-7z"/>
          </svg>
        </button>
        <!-- Volume — hidden on mobile -->
        <div class="audio-vol">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style="flex-shrink:0;opacity:.7">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
          </svg>
          <input type="range" v-model="audioVolume" min="0" max="1" step="0.05" class="audio-vol-slider">
        </div>
      </div>

      <!-- Compass -->
      <div v-if="showCompass" class="compass-wrapper">
        <Compass :heading="compassHeading" :show-by-default="false" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFullscreen } from '@vueuse/core'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { ApiService } from '../services/api'
import { themes } from '../themes'
import { getTemplate, getTemplateList, type ViewerTemplate } from '../templates'
import Compass from './Compass.vue'
import PasswordGate from './PasswordGate.vue'
import FloorPlan from './FloorPlan.vue'
import BrandingOverlay from './BrandingOverlay.vue'
import SceneList from './SceneList.vue'
import NavArrows from './NavArrows.vue'
import SceneTitle from './SceneTitle.vue'
import HotspotRenderer from './HotspotRenderer.vue'
import ZoomControls from './ZoomControls.vue'
import LeadFormModal from './LeadFormModal.vue'
import SharePanel from './SharePanel.vue'
import vteLogoAsset from '../assets/logo-vte-white.png'
import SceneDescription from './SceneDescription.vue'
import TourMap from './TourMap.vue'
import TourPreview from './TourPreview.vue'
import { AnalyticsTracker } from '../lib/analytics'
import type { Scene, TourConfig } from '../types'

const { t } = useI18n()

const props = defineProps<{
  previewToken?: string | null
  tourId: string
  apiUrl?: string
  storageUrl?: string
  initialTheme?: string
  initialTemplate?: string
  allowThemeChange?: boolean
}>()

const emit = defineEmits<{
  'tour-loaded': [meta: { title: string; description: string; image: string; orgName: string | null }]
}>()

// Template system
const template = ref<ViewerTemplate>(getTemplate(props.initialTemplate || 'carousel'))
const templateList = getTemplateList()
const showTemplateMenu = ref(false)

function selectTemplate(id: string) {
  template.value = getTemplate(id)
  showTemplateMenu.value = false
  // Persist choice in URL
  const url = new URL(window.location.href)
  url.searchParams.set('template', id)
  window.history.replaceState({}, '', url.toString())
}

// Password protection state
const passwordRequired = ref(false)
const passwordTourTitle = ref('')
const unpublishedData = ref<{ title: string; status: string; cover_image_url: string | null } | null>(null)
const suspendedData = ref<{ reason: string; title: string } | null>(null)
const passwordCoverImage = ref('')
const passwordGate = ref<InstanceType<typeof PasswordGate>>()

// State
const loading = ref(true)
const loadingText = ref('')
const error = ref<string | null>(null)
const isNotFoundError = computed(() => {
  if (!error.value) return false
  const msg = error.value.toLowerCase()
  return msg.includes('not found') || msg.includes('no scenes') || msg.includes('not available')
})
const errorMessage = computed(() => {
  if (!error.value) return ''
  if (isNotFoundError.value) return 'The tour you are looking for is no longer available.'
  return error.value
})
const tourViewerRoot = ref<HTMLElement>()
const panoramaContainer = ref<HTMLElement>()
const scenes = ref<Scene[]>([])
const floorPlans = ref<any[]>([])
const layoutAlwaysShow = ref(false)
const branding = ref<any>(null)
const showFreeWatermark = ref(false)
let analyticsTracker: AnalyticsTracker | null = null
const activeSceneId = ref<string | null>(null)
const autoRotate = ref(false)
const showControls = ref(true)
const showSceneList = ref(true)
const hasAudio = ref(false)
const isAudioPlaying = ref(false)
const audioVolume = ref(0.5)
const isVRSupported = ref(false)
const currentTheme = ref(props.initialTheme || 'default')
const showCompass = ref(true)
const compassHeading = ref(0)
const compassNorthOffset = ref(0)
const showSharePanel = ref(false)
const showLeadForm = ref(false)
const leadFormEnabled = ref(false)

// Report
const showReportModal = ref(false)
const reportReason = ref('')
const reportDetails = ref('')
const reportSubmitting = ref(false)
const reportSubmitted = ref(false)
const reportError = ref('')

// Track scene list height so the flag button doesn't overlap the collapse toggle
const sceneListWrapperEl = ref<InstanceType<typeof SceneList> | null>(null)
const sceneListHeight = ref(0)
const reportButtonBottom = computed(() => {
  if (template.value.sceneListPosition === 'bottom') {
    // The collapse toggle sits `top: -32px` above the wrapper (outside its bounding box).
    // Add 48px (32px toggle + 16px gap) so the flag always clears it, even when collapsed.
    const h = sceneListHeight.value || 0
    return `${h + 48}px`
  }
  return '16px'
})
const reportReasons = [
  { key: 'illegal', label: 'report.illegal' },
  { key: 'explicit', label: 'report.explicit' },
  { key: 'gambling', label: 'report.gambling' },
  { key: 'violence', label: 'report.violence' },
  { key: 'harassment', label: 'report.harassment' },
  { key: 'copyright', label: 'report.copyright' },
  { key: 'other', label: 'report.other' },
]

async function submitReport() {
  if (!reportReason.value) return
  reportSubmitting.value = true
  reportError.value = ''
  try {
    const res = await fetch(`${props.apiUrl}/api/reports`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tour_uuid: props.tourId,
        reason: reportReason.value,
        details: reportDetails.value || null,
      }),
    })
    if (!res.ok) throw new Error('Failed to submit report')
    reportSubmitted.value = true
  } catch (e: any) {
    reportError.value = e.message || 'Failed to submit'
  } finally {
    reportSubmitting.value = false
  }
}
const showPreview = ref(false) // Will be set based on embed param
const tourTitle = ref('')
const tourDescription = ref('')
const tourCoverImage = ref('')
const tourLat = ref<number | null>(null)
const tourLng = ref<number | null>(null)
const googleMapsApiKey = ref<string | null>(null)
const cameraChanged = ref(0) // Increment to trigger hotspot updates

// Computed
const tourId = computed(() => props.tourId)
const activeScene = computed(() => scenes.value.find(s => String(s.id) === String(activeSceneId.value)))
const activeSceneIndex = computed(() => scenes.value.findIndex(s => String(s.id) === String(activeSceneId.value)))

// Default VTE branding — imported as Vite asset so it's bundled and hashed (avoids 404 in Workers SPA mode)
const vteLogo = vteLogoAsset
const defaultBranding = {
  logo_url: vteLogo,
  watermark_text: 'virtualtoureasy.com',
  watermark_enabled: true,
  powered_by_enabled: true,
  company_name: 'VirtualTourEasy',
}

const legacyBrandingRef = ref<any>(null)

const effectiveBranding = computed(() => {
  const b = branding.value || legacyBrandingRef.value || defaultBranding
  if (!b) return defaultBranding
  // Plan gating: on free plan (show_watermark = true), suppress custom logo
  if (showFreeWatermark.value && b.logo_url) {
    return { ...b, logo_url: null, logo_enabled: false }
  }
  return b
})

// Three.js objects (camera exposed as ref for hotspot projection)
const threeCamera = ref<THREE.PerspectiveCamera | null>(null)
let camera: THREE.PerspectiveCamera
let scene3d: THREE.Scene
let renderer: THREE.WebGLRenderer
let controls: OrbitControls
let audioListener: THREE.AudioListener
let audioSource: THREE.PositionalAudio | null = null
let globalAudioEl: HTMLAudioElement | null = null  // Global background audio (legacy tours)
let currentTexture: THREE.Texture | null = null
let currentGeometry: THREE.SphereGeometry | null = null
let currentMaterial: THREE.MeshBasicMaterial | null = null
let nadirMesh: THREE.Mesh | null = null

// Fullscreen
const { isFullscreen, isSupported: isFullscreenSupported, toggle: toggleFullscreen } = useFullscreen(tourViewerRoot)

// Resize when fullscreen changes
watch(isFullscreen, () => {
  nextTick(() => {
    onResize()
  })
})

// Navigation
function prevScene() {
  if (scenes.value.length < 2) return
  const idx = activeSceneIndex.value
  const prevIdx = idx <= 0 ? scenes.value.length - 1 : idx - 1
  switchScene(scenes.value[prevIdx].id)
}

function nextScene() {
  if (scenes.value.length < 2) return
  const idx = activeSceneIndex.value
  const nextIdx = idx >= scenes.value.length - 1 ? 0 : idx + 1
  switchScene(scenes.value[nextIdx].id)
}

// Initialize viewer
onMounted(async () => {
  loadingText.value = t('viewer.loadingTour')
  // Safety timeout — never spin forever
  const loadTimeout = setTimeout(() => {
    if (loading.value) {
      error.value = 'Tour took too long to load'
      loading.value = false
    }
  }, 30000)
  try {
    // Get template/theme from URL
    const params = new URLSearchParams(window.location.search)
    const urlTemplate = params.get('template')
    if (urlTemplate) template.value = getTemplate(urlTemplate)
    const urlTheme = params.get('theme')
    if (urlTheme && themes[urlTheme]) currentTheme.value = urlTheme

    await initThreeJS()
    await loadTourConfig()
    animate()
    window.addEventListener('resize', onResize)
    window.addEventListener('keydown', onKeyDown)
    isVRSupported.value = await checkVRSupport()
  } catch (e) {
    error.value = e instanceof Error ? e.message : t('viewer.errorLoading')
  } finally {
    clearTimeout(loadTimeout)
    loading.value = false
    nextTick(() => onResize())
  }
})

// Scene list resize observer — keeps flag button above the panel
let sceneListObserver: ResizeObserver | null = null
watch(sceneListWrapperEl, (instance) => {
  sceneListObserver?.disconnect()
  const el = (instance as any)?.$el as HTMLElement | undefined
  if (!el) { sceneListHeight.value = 0; return }
  sceneListObserver = new ResizeObserver(() => {
    sceneListHeight.value = el.offsetHeight
  })
  sceneListObserver.observe(el)
  sceneListHeight.value = el.offsetHeight
})

onUnmounted(() => {
  sceneListObserver?.disconnect()
  if (analyticsTracker) {
    analyticsTracker.destroy()
    analyticsTracker = null
  }
  window.removeEventListener('resize', onResize)
  window.removeEventListener('keydown', onKeyDown)
  if (renderer) renderer.setAnimationLoop(null)
  disposeAudio()
  disposeSceneResources()
  removeNadir()
  if (audioListener) camera?.remove(audioListener)
  disposeGlobalAudio()
  controls?.dispose()
  if (renderer) {
    renderer.dispose()
    renderer.forceContextLoss()
    renderer.domElement.parentNode?.removeChild(renderer.domElement)
  }
})

function disposeAudio() {
  if (audioSource) {
    if (audioSource.isPlaying) audioSource.stop()
    audioSource.disconnect()
    if (audioSource.buffer) audioSource.setBuffer(null as any)
    audioSource = null
  }
  // Don't clear hasAudio/isAudioPlaying if global audio is still active
  if (!globalAudioEl) {
    isAudioPlaying.value = false
    hasAudio.value = false
  }
}

function disposeGlobalAudio() {
  if (globalAudioEl) {
    globalAudioEl.pause()
    globalAudioEl.src = ''
    globalAudioEl = null
  }
  if (!audioSource) {
    isAudioPlaying.value = false
    hasAudio.value = false
  }
}

function disposeSceneResources() {
  currentTexture?.dispose(); currentTexture = null
  currentGeometry?.dispose(); currentGeometry = null
  currentMaterial?.dispose(); currentMaterial = null
  // Keep nadir — it persists across scene changes
  if (scene3d) {
    scene3d.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        object.geometry?.dispose()
        const mats = Array.isArray(object.material) ? object.material : [object.material]
        mats.forEach(mat => {
          if ('map' in mat && mat.map) mat.map.dispose()
          mat.dispose()
        })
      }
    })
    scene3d.clear()
  }
}

// Nadir — circular branding image at the bottom of the 360° sphere
function createNadir(imageUrl: string) {
  removeNadir()
  const loader = new THREE.TextureLoader()
  loader.crossOrigin = 'anonymous'
  loader.load(imageUrl, (texture) => {
    texture.colorSpace = THREE.SRGBColorSpace
    const geometry = new THREE.CircleGeometry(80, 64)
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: THREE.DoubleSide,
      depthWrite: false,
    })
    nadirMesh = new THREE.Mesh(geometry, material)
    // Position at bottom of sphere (south pole), facing up
    nadirMesh.position.set(0, -490, 0)
    nadirMesh.rotation.x = -Math.PI / 2
    scene3d.add(nadirMesh)
  })
}

function removeNadir() {
  if (nadirMesh) {
    scene3d.remove(nadirMesh)
    nadirMesh.geometry.dispose()
    ;(nadirMesh.material as THREE.MeshBasicMaterial).map?.dispose()
    ;(nadirMesh.material as THREE.MeshBasicMaterial).dispose()
    nadirMesh = null
  }
}

function retryLoad() {
  error.value = ''
  loading.value = true
  loadTourConfig()
}

async function loadTourConfig() {
  try {
    const api = new ApiService({ apiUrl: props.apiUrl, storageUrl: props.storageUrl })
    const tourConfig = await api.getTourConfig(props.tourId, props.previewToken) as any

    // Suspended tour or blocked account
    if (tourConfig.suspended) {
      suspendedData.value = {
        reason: tourConfig.reason || 'tour_suspended',
        title: tourConfig.title || 'Tour',
      }
      loading.value = false
      return
    }

    // Unpublished tour
    if (tourConfig.unpublished) {
      unpublishedData.value = {
        title: tourConfig.title || 'Untitled Tour',
        status: tourConfig.status || 'draft',
        cover_image_url: tourConfig.cover_image_url || null,
      }
      loading.value = false
      return
    }

    if (tourConfig.password_required) {
      passwordRequired.value = true
      passwordTourTitle.value = tourConfig.title || ''
      passwordCoverImage.value = tourConfig.cover_image_url || ''
      loading.value = false
      return
    }

    scenes.value = tourConfig.scenes
    floorPlans.value = tourConfig.floor_plans || []
    layoutAlwaysShow.value = tourConfig.viewer_config?.layout_always_show || false
    branding.value = tourConfig.branding || null

    // Force watermark for free plan tours
    if (tourConfig.show_watermark) {
      showFreeWatermark.value = true
    }

    // Convert legacy tour_config.branding to viewer format if D1 branding is null
    const legacyBranding = tourConfig.tour_config?.branding
    const storageBase = tourConfig.storage_base_url || ''
    if (!tourConfig.branding && legacyBranding) {
      legacyBrandingRef.value = {
        logo_url: legacyBranding.logo && legacyBranding.logoImageUrl
          ? `${storageBase}/${legacyBranding.logoImageUrl}`
          : null,
        company_name: legacyBranding.copyright || null,
        watermark_enabled: false,
        powered_by_enabled: true,
      }
    }

    // Nadir (branding image at bottom of sphere)
    // D1 branding: nadir=1 + nadir_image_url
    // Legacy tour_config.branding: nadir = "upload/org/tour/nadir.jpg" (relative path)
    const b = tourConfig.branding
    let nadirUrl: string | null = null
    if (b && (b.nadir === 1 || b.nadir === true) && b.nadir_image_url) {
      nadirUrl = b.nadir_image_url
    } else if (legacyBranding?.nadir && typeof legacyBranding.nadir === 'string') {
      // Legacy nadir is a relative path like "upload/1104/1435/nadir.jpg"
      nadirUrl = `${storageBase}/${legacyBranding.nadir}`
    }
    if (nadirUrl) {
      createNadir(nadirUrl)
    }

    // Tour metadata
    tourTitle.value = tourConfig.title || 'Virtual Tour'
    tourDescription.value = tourConfig.description || ''
    tourCoverImage.value = tourConfig.cover_image_url || scenes.value[0]?.image || ''
    tourLat.value = tourConfig.latitude || tourConfig.lat || null
    tourLng.value = tourConfig.longitude || tourConfig.lng || null

    // Emit SEO metadata so App.vue can update document.title + meta tags
    emit('tour-loaded', {
      title: tourConfig.title || '',
      description: tourConfig.description || '',
      image: tourCoverImage.value,
      orgName: tourConfig.branding?.company_name || tourConfig.organization?.company_name || null
    })

    // Global background audio — supports both legacy (string) and new object format
    const audioStorageBase = tourConfig.storage_base_url || props.storageUrl || ''
    const audioConfig = tourConfig.audio
    // New format: { backgroundUrl, volume, autoplay, loop }
    // Legacy format: plain string URL
    const rawAudioUrl = typeof audioConfig === 'string'
      ? audioConfig
      : (audioConfig?.backgroundUrl || null)
    const audioSettings = {
      volume: typeof audioConfig === 'object' ? (audioConfig?.volume ?? 0.5) : 0.5,
      autoplay: typeof audioConfig === 'object' ? (audioConfig?.autoplay ?? false) : false,
      loop: typeof audioConfig === 'object' ? (audioConfig?.loop ?? true) : true,
    }
    const globalAudioUrl = rawAudioUrl ? (() => {
      if (!rawAudioUrl.startsWith('http')) return `${audioStorageBase}/${rawAudioUrl.replace(/^\//, '')}`
      const legacyMatch = rawAudioUrl.match(/virtualtoureasy\.com\/tour\/(upload|360app)\/(.+)/)
      if (legacyMatch) return `${audioStorageBase}/${legacyMatch[1]}/${legacyMatch[2]}`
      return rawAudioUrl
    })() : null
    if (globalAudioUrl && !globalAudioEl) {
      globalAudioEl = new Audio(globalAudioUrl)
      globalAudioEl.loop = audioSettings.loop
      globalAudioEl.volume = audioSettings.volume
      audioVolume.value = audioSettings.volume
      globalAudioEl.preload = 'auto'
      hasAudio.value = true
      // Autoplay on user interaction (browsers block autoplay without gesture)
      const tryAutoplay = () => {
        if (globalAudioEl && !isAudioPlaying.value) {
          globalAudioEl.play().then(() => { isAudioPlaying.value = true }).catch(() => {})
        }
        document.removeEventListener('click', tryAutoplay)
        document.removeEventListener('touchstart', tryAutoplay)
      }
      if (audioSettings.autoplay) {
        document.addEventListener('click', tryAutoplay, { once: true })
        document.addEventListener('touchstart', tryAutoplay, { once: true })
      }
    }
    googleMapsApiKey.value = tourConfig.branding?.google_maps_api_key || tourConfig.viewer_config?.googleMapsApiKey || null
    leadFormEnabled.value = !!tourConfig.lead_form_enabled

    // Set OG meta tags for social sharing
    setMetaTags(tourConfig)

    // Show preview/landing page unless in embed mode or autoplay
    const params = new URLSearchParams(window.location.search)
    const isEmbed = params.get('embed') === '1' || window.self !== window.top
    const isAutoplay = params.get('autoplay') === '1' || tourConfig.viewer_config?.autoplay
    showPreview.value = !isEmbed && !isAutoplay

    // Apply template from tour config if set
    if (tourConfig.viewer_config?.template) {
      const params = new URLSearchParams(window.location.search)
      if (!params.get('template')) {
        template.value = getTemplate(tourConfig.viewer_config.template)
      }
    }

    // Apply branding colors as primary color for template
    if (tourConfig.branding?.colors?.primary) {
      document.documentElement.style.setProperty('--brand-primary', tourConfig.branding.colors.primary)
    }

    if (tourConfig.startScene && scenes.value.length > 0) {
      await loadScene(tourConfig.startScene)
    } else if (scenes.value.length > 0) {
      await loadScene(scenes.value[0].id)
    } else {
      throw new Error(t('viewer.noScenes'))
    }

    showControls.value = tourConfig.viewer_config?.controls ?? true
    showSceneList.value = tourConfig.viewer_config?.sceneList ?? true
    autoRotate.value = tourConfig.viewer_config?.autoRotate ?? false
    if (autoRotate.value) {
      controls.autoRotate = true
      controls.autoRotateSpeed = tourConfig.viewer_config?.autoRotateSpeed ?? 2
    }

    if (!props.initialTheme && tourConfig.viewer_config?.theme) {
      currentTheme.value = tourConfig.viewer_config.theme
    }

    showCompass.value = tourConfig.viewer_config?.compass ?? true

    // Initialize analytics tracker
    try {
      const apiBase = props.apiUrl || new URL(window.location.href).origin
      analyticsTracker = new AnalyticsTracker(apiBase, tourConfig.uuid || props.tourId)
      analyticsTracker.trackTourView()
    } catch {}
  } catch (e) {
    throw new Error(t('errors.tourLoadFailed'))
  }
}

async function handlePasswordVerify(password: string) {
  try {
    const api = new ApiService({ apiUrl: props.apiUrl, storageUrl: props.storageUrl })
    const tourConfig = await api.verifyTourPassword(props.tourId, password)
    passwordRequired.value = false
    loading.value = true
    loadingText.value = t('viewer.loadingTour')
    scenes.value = tourConfig.scenes || []
    if (tourConfig.startScene && scenes.value.length > 0) {
      await loadScene(tourConfig.startScene)
    } else if (scenes.value.length > 0) {
      await loadScene(scenes.value[0].id)
    }
    showControls.value = tourConfig.viewer_config?.controls ?? true
    showSceneList.value = tourConfig.viewer_config?.sceneList ?? true
    autoRotate.value = tourConfig.viewer_config?.autoRotate ?? false
    loading.value = false
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Failed to verify password'
    passwordGate.value?.setError(msg)
  }
}

async function initThreeJS() {
  const container = panoramaContainer.value
  if (!container) return
  scene3d = new THREE.Scene()
  camera = new THREE.PerspectiveCamera(100, container.clientWidth / container.clientHeight, 0.1, 1000)
  camera.position.z = 0.1
  threeCamera.value = camera
  renderer = new THREE.WebGLRenderer({ antialias: true })
  renderer.setSize(container.clientWidth, container.clientHeight)
  renderer.setPixelRatio(window.devicePixelRatio)
  renderer.xr.enabled = true
  container.appendChild(renderer.domElement)
  controls = new OrbitControls(camera, renderer.domElement)
  controls.enableZoom = false // We handle zoom via FOV, not distance
  controls.enablePan = false
  controls.enableDamping = true
  controls.dampingFactor = 0.1
  controls.rotateSpeed = -0.25
  // Handle mouse wheel zoom via FOV
  renderer.domElement.addEventListener('wheel', (e: WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 3 : -3
    camera.fov = Math.max(30, Math.min(100, camera.fov + delta))
    camera.updateProjectionMatrix()
  }, { passive: false })

  // Pinch-to-zoom on touch devices
  let lastPinchDist = 0
  let isPinching = false

  renderer.domElement.addEventListener('touchstart', (e: TouchEvent) => {
    if (e.touches.length === 2) {
      isPinching = true
      const dx = e.touches[0].clientX - e.touches[1].clientX
      const dy = e.touches[0].clientY - e.touches[1].clientY
      lastPinchDist = Math.sqrt(dx * dx + dy * dy)
    }
  }, { passive: true })

  renderer.domElement.addEventListener('touchmove', (e: TouchEvent) => {
    if (e.touches.length === 2 && isPinching) {
      const dx = e.touches[0].clientX - e.touches[1].clientX
      const dy = e.touches[0].clientY - e.touches[1].clientY
      const dist = Math.sqrt(dx * dx + dy * dy)
      const delta = (lastPinchDist - dist) * 0.15 // sensitivity
      camera.fov = Math.max(30, Math.min(100, camera.fov + delta))
      camera.updateProjectionMatrix()
      lastPinchDist = dist
    }
  }, { passive: true })

  renderer.domElement.addEventListener('touchend', () => {
    isPinching = false
    lastPinchDist = 0
  }, { passive: true })

  audioListener = new THREE.AudioListener()
  camera.add(audioListener)
}

function animate() {
  renderer.setAnimationLoop(() => {
    controls.update()
    renderer.render(scene3d, camera)
    updateCompassHeading()
    // Throttled: only increment every 2nd frame (~30fps) to reduce hotspot reactivity
    cameraChanged.value++
  })
}

function updateCompassHeading() {
  if (!camera || !controls) return
  const direction = new THREE.Vector3()
  camera.getWorldDirection(direction)
  let yaw = Math.atan2(direction.x, direction.z) * (180 / Math.PI)
  yaw = (yaw + 360) % 360
  let heading = yaw - compassNorthOffset.value
  while (heading < 0) heading += 360
  while (heading >= 360) heading -= 360
  compassHeading.value = heading
}

/**
 * Resolve the initial view from a scene object.
 * API returns snake_case fields (initial_yaw, initial_pitch, initial_zoom).
 * Legacy camelCase nested object (initialView) is also supported as fallback.
 */
function resolveInitialView(s: any): { yaw: number; pitch: number; zoom: number } | null {
  // New API format: snake_case separate fields
  if (s.initial_yaw !== undefined || s.initial_pitch !== undefined || s.initial_zoom !== undefined) {
    return {
      yaw: Number(s.initial_yaw ?? 0),
      pitch: Number(s.initial_pitch ?? 0),
      zoom: Number(s.initial_zoom ?? 1),
    }
  }
  // Legacy camelCase nested object
  if (s.initialView) {
    return {
      yaw: Number(s.initialView.yaw ?? 0),
      pitch: Number(s.initialView.pitch ?? 0),
      zoom: Number(s.initialView.zoom ?? 1),
    }
  }
  return null
}

async function loadScene(sceneId: string | number) {
  const sceneData = scenes.value.find(s => String(s.id) === String(sceneId))
  if (!sceneData) return

  // Track scene view
  if (analyticsTracker) {
    analyticsTracker.trackSceneView(String(sceneId))
  }

  loadingText.value = t('viewer.loadingScene')
  loading.value = true
  try {
    disposeAudio()
    disposeSceneResources()
    const loader = new THREE.TextureLoader()
    loader.crossOrigin = 'anonymous'
    const texture = await new Promise<THREE.Texture>((resolve, reject) => {
      loader.load(sceneData.image, resolve, undefined, reject)
    })
    // Maximize texture quality
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.generateMipmaps = false
    if (renderer) {
      texture.anisotropy = renderer.capabilities.getMaxAnisotropy()
    }
    texture.colorSpace = THREE.SRGBColorSpace
    currentTexture = texture
    const geometry = new THREE.SphereGeometry(500, 64, 40)
    geometry.scale(-1, 1, 1)
    currentGeometry = geometry
    const material = new THREE.MeshBasicMaterial({ map: texture })
    currentMaterial = material
    const sphere = new THREE.Mesh(geometry, material)
    scene3d.add(sphere)
    const initView = resolveInitialView(sceneData)
    if (initView) {
      const yawRad = THREE.MathUtils.degToRad(-initView.yaw)
      const pitchRad = THREE.MathUtils.degToRad(initView.pitch)
      const radius = 0.1
      camera.position.set(
        radius * Math.cos(pitchRad) * Math.sin(yawRad),
        radius * Math.sin(pitchRad),
        radius * Math.cos(pitchRad) * Math.cos(yawRad)
      )
      controls.target.set(0, 0, 0)
      // Apply initial zoom — legacy tours use FOV (≥20), modern uses multiplier (≤2)
      const fov = initView.zoom
      if (fov && fov >= 20 && fov <= 120) {
        camera.fov = fov
      } else if (fov && fov > 0 && fov <= 2) {
        camera.fov = Math.max(30, Math.min(100, 75 / fov))
      }
      camera.updateProjectionMatrix()
      controls.update()
    }
    activeSceneId.value = sceneId
    if ((sceneData as any).compassNorthOffset !== undefined) {
      compassNorthOffset.value = (sceneData as any).compassNorthOffset
    }
    if (sceneData.audio) {
      hasAudio.value = true
      const audioLoader = new THREE.AudioLoader()
      audioSource = new THREE.PositionalAudio(audioListener)
      audioLoader.load(sceneData.audio.url, (buffer) => {
        if (audioSource) {
          audioSource.setBuffer(buffer)
          audioSource.setLoop(sceneData.audio?.loop ?? false)
          audioSource.setVolume(audioVolume.value)
          if (sceneData.audio?.autoplay) {
            audioSource.play()
            isAudioPlaying.value = true
          }
        }
      })
      sphere.add(audioSource)
    }
  } catch (e) {
    console.error('Error loading scene:', e)
    error.value = t('errors.sceneLoadFailed')
  } finally {
    loading.value = false
    nextTick(() => onResize())
  }
}

async function switchScene(sceneId: string | number) {
  if (String(sceneId) === String(activeSceneId.value)) return
  await loadScene(sceneId)
}

function handleHotspotNavigate(sceneId: string) {
  if (analyticsTracker) {
    analyticsTracker.trackHotspotClick(sceneId, activeSceneId.value || undefined)
  }
  switchScene(sceneId)
}

function toggleAutoRotate() {
  autoRotate.value = !autoRotate.value
  controls.autoRotate = autoRotate.value
}

function recenterView() {
  if (!camera || !controls) return
  // Reset to current scene's initial view
  const sceneData = activeScene.value
  const initView = sceneData ? resolveInitialView(sceneData) : null
  if (initView) {
    const yawRad = THREE.MathUtils.degToRad(-initView.yaw)
    const pitchRad = THREE.MathUtils.degToRad(initView.pitch)
    const radius = 0.1
    camera.position.set(
      radius * Math.cos(pitchRad) * Math.sin(yawRad),
      radius * Math.sin(pitchRad),
      radius * Math.cos(pitchRad) * Math.cos(yawRad)
    )
    controls.target.set(0, 0, 0)
    const fov = initView.zoom
    if (fov && fov >= 20 && fov <= 120) {
      camera.fov = fov
    } else {
      camera.fov = 100
    }
  } else {
    // Default: look forward, standard FOV
    camera.position.set(0, 0, 0.1)
    controls.target.set(0, 0, 0)
    camera.fov = 100
  }
  camera.updateProjectionMatrix()
  controls.update()
}

// VR / Gyroscope
const gyroscopeActive = ref(false)
const xrPresenting = ref(false)
const vrActive = computed(() => gyroscopeActive.value || xrPresenting.value)
let gyroscopeMode = false
let deviceOrientationHandler: ((e: DeviceOrientationEvent) => void) | null = null
let stereoEffect: { render: (s: THREE.Scene, c: THREE.Camera) => void } | null = null

async function toggleVR() {
  if (!isVRSupported.value) return
  try {
    let webxrAvailable = false
    if ('xr' in navigator) {
      try {
        // @ts-ignore
        webxrAvailable = await navigator.xr?.isSessionSupported('immersive-vr')
      } catch {}
    }
    if (webxrAvailable) {
      if (renderer.xr.isPresenting) {
        const session = renderer.xr.getSession()
        if (session) await session.end()
      } else {
        // @ts-ignore
        const session = await navigator.xr?.requestSession('immersive-vr', { optionalFeatures: ['local-floor'] })
        if (session) {
          renderer.xr.setSession(session)
          xrPresenting.value = true
          session.addEventListener('end', () => {
            renderer.xr.setSession(null)
            xrPresenting.value = false
          }, { once: true })
        }
      }
    } else {
      gyroscopeMode ? exitGyroscopeMode() : await enterGyroscopeMode()
    }
  } catch (e) {
    console.error('Failed to toggle VR:', e)
  }
}

async function enterGyroscopeMode() {
  // @ts-ignore
  if (typeof DeviceOrientationEvent?.requestPermission === 'function') {
    try {
      // @ts-ignore
      const permission = await DeviceOrientationEvent.requestPermission()
      if (permission !== 'granted') return
    } catch { return }
  }
  gyroscopeMode = true
  gyroscopeActive.value = true
  controls.enabled = false
  const originalRender = renderer.render.bind(renderer)
  stereoEffect = {
    render(s: THREE.Scene, c: THREE.Camera) {
      const hw = renderer.domElement.width / 2
      const fh = renderer.domElement.height
      renderer.setViewport(0, 0, hw, fh)
      renderer.setScissor(0, 0, hw, fh)
      renderer.setScissorTest(true)
      originalRender(s, c)
      renderer.setViewport(hw, 0, hw, fh)
      renderer.setScissor(hw, 0, hw, fh)
      originalRender(s, c)
      renderer.setScissorTest(false)
    }
  }
  const euler = new THREE.Euler()
  const quaternion = new THREE.Quaternion()
  const orientQ = new THREE.Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5))
  deviceOrientationHandler = (event: DeviceOrientationEvent) => {
    if (event.alpha === null || event.beta === null || event.gamma === null) return
    euler.set(THREE.MathUtils.degToRad(event.beta!), THREE.MathUtils.degToRad(event.alpha!), -THREE.MathUtils.degToRad(event.gamma!), 'YXZ')
    quaternion.setFromEuler(euler).multiply(orientQ)
    const screenQ = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), -(window.screen.orientation?.angle || 0) * Math.PI / 180)
    quaternion.multiply(screenQ)
    camera.quaternion.copy(quaternion)
  }
  window.addEventListener('deviceorientation', deviceOrientationHandler, true)
  // Enter fullscreen on VR mode (use tourViewerRoot so controls stay visible)
  try {
    const el = tourViewerRoot.value || panoramaContainer.value
    if (el && !document.fullscreenElement) {
      await (el.requestFullscreen?.() || (el as any).webkitRequestFullscreen?.())
    }
  } catch {}
  renderer.setAnimationLoop(() => {
    stereoEffect ? stereoEffect.render(scene3d, camera) : renderer.render(scene3d, camera)
    updateCompassHeading()
  })
}

function exitGyroscopeMode() {
  gyroscopeMode = false
  gyroscopeActive.value = false
  controls.enabled = true
  stereoEffect = null
  if (deviceOrientationHandler) {
    window.removeEventListener('deviceorientation', deviceOrientationHandler, true)
    deviceOrientationHandler = null
  }
  renderer.setScissorTest(false)
  renderer.setViewport(0, 0, renderer.domElement.width, renderer.domElement.height)
  renderer.setAnimationLoop(() => {
    controls.update()
    renderer.render(scene3d, camera)
    updateCompassHeading()
  })
  const fse = document.fullscreenElement || (document as any).webkitFullscreenElement
  if (fse) (document.exitFullscreen?.() || (document as any).webkitExitFullscreen?.())
  onResize()
}

async function checkVRSupport() {
  // Only show VR on actually compatible devices
  if ('xr' in navigator) {
    try {
      // @ts-ignore
      if (await navigator.xr?.isSessionSupported('immersive-vr')) return true
    } catch {}
  }
  // Mobile with gyroscope — show for cardboard/gyro mode
  if (typeof DeviceOrientationEvent !== 'undefined' && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) return true
  return false
}

function toggleAudio() {
  if (audioSource) {
    isAudioPlaying.value ? audioSource.pause() : audioSource.play()
    isAudioPlaying.value = !isAudioPlaying.value
  } else if (globalAudioEl) {
    if (isAudioPlaying.value) {
      globalAudioEl.pause()
    } else {
      globalAudioEl.play().catch(() => {})
    }
    isAudioPlaying.value = !isAudioPlaying.value
  }
}

watch(audioVolume, (v) => {
  if (audioSource) audioSource.setVolume(v)
  if (globalAudioEl) globalAudioEl.volume = v
})

function onResize() {
  if (!camera || !renderer || !panoramaContainer.value) return
  camera.aspect = panoramaContainer.value.clientWidth / panoramaContainer.value.clientHeight
  camera.updateProjectionMatrix()
  renderer.setSize(panoramaContainer.value.clientWidth, panoramaContainer.value.clientHeight)
}

// Zoom controls
function zoomIn() {
  if (!camera) return
  camera.fov = Math.max(30, camera.fov - 5)
  camera.updateProjectionMatrix()
}

function zoomOut() {
  if (!camera) return
  camera.fov = Math.min(100, camera.fov + 5)
  camera.updateProjectionMatrix()
}

// Keyboard navigation — OrbitControls doesn't expose rotateLeft/rotateUp publicly,
// so we manipulate the camera position via spherical coordinates directly.
function rotateCamera(deltaAzimuth: number, deltaPolar: number) {
  if (!camera || !controls) return
  const target = controls.target
  const offset = camera.position.clone().sub(target)
  const spherical = new THREE.Spherical().setFromVector3(offset)
  spherical.theta -= deltaAzimuth
  spherical.phi -= deltaPolar
  // Clamp phi to avoid flipping
  spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.phi))
  offset.setFromSpherical(spherical)
  camera.position.copy(target).add(offset)
  camera.lookAt(target)
  controls.update()
}

function onKeyDown(e: KeyboardEvent) {
  if (!controls) return
  const rotateSpeed = 0.05
  switch (e.key) {
    case 'ArrowLeft':
      rotateCamera(rotateSpeed, 0)
      e.preventDefault()
      break
    case 'ArrowRight':
      rotateCamera(-rotateSpeed, 0)
      e.preventDefault()
      break
    case 'ArrowUp':
      rotateCamera(0, rotateSpeed)
      e.preventDefault()
      break
    case 'ArrowDown':
      rotateCamera(0, -rotateSpeed)
      e.preventDefault()
      break
    case '+':
    case '=':
      zoomIn()
      e.preventDefault()
      break
    case '-':
    case '_':
      zoomOut()
      e.preventDefault()
      break
    case ' ':
      toggleAutoRotate()
      e.preventDefault()
      break
  }
}

// Preview / Landing page
function startTour() {
  showPreview.value = false
  nextTick(() => onResize())
}

// Set OG meta tags for social sharing
function setMetaTags(config: any) {
  const setMeta = (prop: string, content: string) => {
    let el = document.querySelector(`meta[property="${prop}"]`) || document.querySelector(`meta[name="${prop}"]`)
    if (!el) {
      el = document.createElement('meta')
      if (prop.startsWith('og:') || prop.startsWith('twitter:')) {
        el.setAttribute(prop.startsWith('og:') ? 'property' : 'name', prop)
      }
      document.head.appendChild(el)
    }
    el.setAttribute('content', content)
  }

  const title = config.title || 'Virtual Tour'
  const desc = config.description || `${title} - 360° Virtual Tour powered by VirtualTourEasy`
  const image = config.cover_image_url || (config.scenes?.[0]?.image_url) || ''
  const url = window.location.href

  document.title = `${title} - 360° Virtual Tour`

  setMeta('og:title', title)
  setMeta('og:description', desc)
  setMeta('og:image', image)
  setMeta('og:url', url)
  setMeta('og:type', 'website')
  setMeta('twitter:card', 'summary_large_image')
  setMeta('twitter:title', title)
  setMeta('twitter:description', desc)
  setMeta('twitter:image', image)
  setMeta('description', desc)

  // Private tours (link-only / unlisted) should not be indexed by search bots
  // 'private' = blocked, 'unlisted' = link-only — both should be noindex
  const robotsValue = (config.visibility === 'private' || config.visibility === 'unlisted') ? 'noindex, nofollow' : 'index, follow'
  let robotsEl = document.querySelector('meta[name="robots"]')
  if (!robotsEl) {
    robotsEl = document.createElement('meta')
    robotsEl.setAttribute('name', 'robots')
    document.head.appendChild(robotsEl)
  }
  robotsEl.setAttribute('content', robotsValue)
}
</script>

<style>
.tour-viewer {
  position: relative;
  width: 100%;
  height: 100%;
  max-height: 100vh;
  max-height: 100dvh;
  overflow: hidden;
  font-family: var(--brand-font, -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif);
}

/* Loading */
.loading-screen {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  z-index: 1000;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.vte-loader-logo {
  height: 36px;
  opacity: 0.7;
}

.loading-spinner {
  width: 36px; height: 36px;
  border: 3px solid rgba(59, 130, 246, 0.3);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loading-text {
  color: rgba(255,255,255,0.6);
  font-size: 13px;
}

/* Error */
/* Unpublished tour screen */
.unpublished-screen {
  position: absolute;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  overflow: hidden;
}

.unpublished-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.unpublished-bg-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(20px) brightness(0.3);
  transform: scale(1.1);
}

.unpublished-card {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 480px;
  padding: 3rem 2.5rem;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 20px;
  backdrop-filter: blur(16px);
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.4);
}

.unpublished-icon {
  margin-bottom: 1.25rem;
  opacity: 0.7;
}

.unpublished-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #f8fafc;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
}

.unpublished-badge {
  display: inline-block;
  padding: 0.5rem 1.25rem;
  background: rgba(148, 163, 184, 0.15);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 24px;
  color: #cbd5e1;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 1.25rem;
}

.unpublished-hint {
  color: #64748b;
  font-size: 0.875rem;
  margin-bottom: 2rem;
}

.unpublished-link {
  display: inline-block;
  color: #64748b;
  text-decoration: none;
  font-size: 0.75rem;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.unpublished-link:hover {
  opacity: 1;
}

.error-screen {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  z-index: 1000;
}

.error-card {
  text-align: center;
  max-width: 400px;
  padding: 2.5rem 2rem;
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 16px;
  backdrop-filter: blur(12px);
}

.error-icon-wrap { margin-bottom: 1.25rem; }
.error-title { color: #f8fafc; font-size: 1.35rem; font-weight: 600; margin-bottom: 0.5rem; letter-spacing: -0.02em; }
.error-detail { color: #cbd5e1; font-size: 0.95rem; line-height: 1.5; margin-bottom: 0.5rem; }
.error-hint { color: #64748b; font-size: 0.8rem; line-height: 1.6; margin-bottom: 1.75rem; }

.error-retry {
  display: inline-block;
  padding: 0.6rem 1.75rem;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white; border: none; border-radius: 8px;
  font-size: 0.875rem; font-weight: 500; cursor: pointer;
  transition: all 0.2s; margin-bottom: 1rem;
}
.error-retry:hover { background: linear-gradient(135deg, #2563eb, #1d4ed8); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3); }
.error-home {
  display: inline-block; color: #94a3b8; font-size: 0.8rem; text-decoration: none;
  padding: 0.5rem 1rem; border-radius: 6px; transition: all 0.2s;
}
.error-home:hover { color: #e2e8f0; background: rgba(148, 163, 184, 0.1); }

/* Tour container layout */
.tour-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.panorama-container {
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 1;
}

/* Ensure canvas doesn't block hotspot clicks */
.panorama-container canvas {
  position: absolute;
  top: 0;
  left: 0;
}

/* Fullscreen stacking fix */
.tour-viewer:fullscreen,
.tour-viewer:-webkit-full-screen {
  width: 100vw;
  height: 100vh;
  background: black;
}

.tour-viewer:fullscreen .tour-container,
.tour-viewer:-webkit-full-screen .tour-container {
  position: relative;
  width: 100%;
  height: 100%;
}

/* empty */

/* Controls */
.controls-container {
  position: absolute;
  display: flex;
  gap: 4px;
  padding: 6px;
  z-index: 14;
}

/* All positions resolve to centered above scene list */
.controls-bottom-center,
.controls-bottom-left,
.controls-bottom-right,
.controls-top-center,
.controls-top-right {
  bottom: 90px;
  left: 50%;
  transform: translateX(-50%);
}

/* Controls styles */
.controls-rounded {
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(8px);
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.1);
}

.controls-pill {
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(8px);
  border-radius: 24px;
  border: 1px solid rgba(255,255,255,0.1);
}

.controls-flat {
  background: rgba(0,0,0,0.5);
  border-radius: 4px;
}

.controls-minimal {
  background: transparent;
  gap: 8px;
}

.controls-container button {
  width: 36px; height: 36px;
  background: none; border: none;
  color: rgba(255,255,255,0.8);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.controls-container button:hover {
  color: white;
  background: rgba(255,255,255,0.1);
}

.controls-container button.active {
  color: var(--brand-primary, #3b82f6);
}

.controls-container .vr-btn svg {
  width: 22px;
  height: 22px;
}

.controls-container .vr-btn.active {
  background: rgba(59, 130, 246, 0.3);
  color: #60a5fa;
}

.controls-minimal button {
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
  border-radius: 50%;
}

/* Template menu */
.template-menu {
  position: absolute;
  bottom: 64px;
  right: 16px;
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 12px;
  padding: 8px;
  z-index: 20;
  min-width: 220px;
}

.template-menu-title {
  color: rgba(255,255,255,0.5);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 4px 8px 8px;
}

.template-option {
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  background: none;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
  color: white;
}

.template-option:hover {
  background: rgba(255,255,255,0.08);
}

.template-option.active {
  background: rgba(59,130,246,0.2);
  border: 1px solid rgba(59,130,246,0.3);
}

.template-option-name {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255,255,255,0.9);
}

.template-option-desc {
  display: block;
  font-size: 11px;
  color: rgba(255,255,255,0.45);
  margin-top: 2px;
}

/* Audio Mini Player */
/* Audio player — compact icon button, top-right before fullscreen (desktop/tablet) */
.audio-player {
  position: absolute;
  top: 16px; right: 112px;
  display: flex; align-items: center; justify-content: center;
  width: 40px; height: 40px;
  padding: 0;
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 8px;
  z-index: 14;
  color: #fff;
}
.audio-player .audio-bars { display: none; }
.audio-player .audio-vol  { display: none; }
/* Note icon when paused, play/pause icon when playing */
.audio-icon-note { display: block; }
.audio-icon-play { display: none; }
.audio-player.audio-playing .audio-icon-note { display: none; }
.audio-player.audio-playing .audio-icon-play { display: block; }

/* Animated bars */
.audio-bars {
  display: flex; align-items: flex-end; gap: 2px;
  height: 14px;
}
.audio-bars span {
  display: block; width: 3px; border-radius: 2px;
  background: var(--brand-primary, #3b82f6);
  height: 4px;
  transition: height 0.2s;
}
.audio-bars.playing span:nth-child(1) { animation: bar 0.8s ease-in-out infinite; }
.audio-bars.playing span:nth-child(2) { animation: bar 0.8s ease-in-out infinite 0.15s; }
.audio-bars.playing span:nth-child(3) { animation: bar 0.8s ease-in-out infinite 0.3s; }
.audio-bars.playing span:nth-child(4) { animation: bar 0.8s ease-in-out infinite 0.45s; }
@keyframes bar {
  0%, 100% { height: 4px; }
  50%       { height: 14px; }
}

.audio-btn {
  background: none;
  border: none; border-radius: 8px;
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
}
.audio-btn:hover { background: rgba(255,255,255,0.15); }
/* Container glows blue when audio is playing */
.audio-player.audio-playing {
  background: rgba(59,130,246,0.75);
  border-color: rgba(59,130,246,0.5);
}

.audio-vol {
  display: flex; align-items: center; gap: 5px;
}
.audio-vol-slider {
  width: 64px;
  accent-color: var(--brand-primary, #3b82f6);
  cursor: pointer;
}

/* Compass — top-left on all devices */
.compass-wrapper {
  position: absolute;
  top: 14px;
  left: 16px;
  right: auto;
  transform: none;
  z-index: 14;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Close template menu when clicking outside */
.tour-viewer { cursor: default; }

/* Fullscreen button — right side, above zoom */
.fullscreen-toggle {
  position: absolute;
  top: 16px;
  right: 64px;
  z-index: 15;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  transition: all 0.2s;
}

.fullscreen-toggle:hover {
  background: rgba(0, 0, 0, 0.8);
  border-color: rgba(255, 255, 255, 0.3);
}

/* Report/flag toggle — bottom offset driven by :style binding to clear scene list */
.report-toggle {
  position: absolute;
  bottom: 16px; /* fallback; overridden by :style binding */
  right: 16px;
  z-index: 15;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  transition: bottom 0.3s ease, background 0.2s, border-color 0.2s, color 0.2s;
}
.report-toggle:hover {
  background: rgba(239, 68, 68, 0.7);
  border-color: rgba(239, 68, 68, 0.5);
  color: white;
}

/* Report Modal */
.report-overlay { position: fixed; inset: 0; z-index: 10000; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; padding: 16px; }
.report-modal { background: #fff; border-radius: 16px; padding: 32px; max-width: 420px; width: 100%; position: relative; box-shadow: 0 20px 60px rgba(0,0,0,0.3); }
.report-close { position: absolute; top: 12px; right: 16px; background: none; border: none; font-size: 24px; cursor: pointer; color: #666; }
.report-title { font-size: 20px; font-weight: 700; color: #1a1a1a; margin-bottom: 4px; }
.report-subtitle { font-size: 14px; color: #666; margin-bottom: 16px; }
.report-reasons { display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px; }
.report-reason { display: flex; align-items: center; gap: 8px; font-size: 14px; color: #333; cursor: pointer; padding: 8px 12px; border-radius: 8px; transition: background 0.15s; }
.report-reason:hover { background: #f5f5f5; }
.report-reason input { accent-color: #ef4444; }
.report-textarea { width: 100%; padding: 10px 14px; border: 1px solid #ddd; border-radius: 8px; font-size: 14px; font-family: inherit; outline: none; resize: vertical; min-height: 60px; box-sizing: border-box; }
.report-textarea:focus { border-color: #ef4444; }
.report-error { color: #ef4444; font-size: 13px; margin: 8px 0 0; }
.report-submit { width: 100%; margin-top: 12px; padding: 12px; background: #ef4444; color: #fff; border: none; border-radius: 8px; font-size: 15px; font-weight: 600; cursor: pointer; }
.report-submit:hover { background: #dc2626; }
.report-submit:disabled { opacity: 0.5; cursor: not-allowed; }
.report-success { text-align: center; padding: 20px 0; }
.report-success-icon { width: 56px; height: 56px; border-radius: 50%; background: #22c55e; color: #fff; font-size: 28px; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
.report-success h3 { font-size: 20px; color: #1a1a1a; margin-bottom: 8px; }
.report-success p { color: #666; font-size: 14px; }

.contact-toggle {
  position: absolute;
  bottom: 68px;  /* above report-toggle: 16px bottom + 40px height + 12px gap */
  right: 16px;
  z-index: 15;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(59, 130, 246, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  transition: all 0.2s;
}
.contact-toggle:hover {
  background: rgba(59, 130, 246, 1);
  border-color: rgba(255, 255, 255, 0.4);
  transform: scale(1.05);
}

.share-toggle {
  position: absolute;
  top: 210px;
  right: 16px;
  z-index: 15;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  transition: all 0.2s;
}

.share-toggle:hover {
  background: rgba(0, 0, 0, 0.8);
  border-color: rgba(255, 255, 255, 0.3);
}

@media (max-width: 768px) {
  .fullscreen-toggle {
    top: 12px;
    right: 54px;
    width: 34px;
    height: 34px;
  }

  .share-toggle {
    top: 190px;
    bottom: auto;
    right: 12px;
    width: 34px;
    height: 34px;
  }

  .report-toggle {
    bottom: 12px;
    right: 12px;
    width: 34px;
    height: 34px;
  }

  .controls-container {
    bottom: 70px !important;
    padding: 4px;
    gap: 2px;
  }

  .controls-container button {
    width: 34px;
    height: 34px;
  }

  /* Audio: move to top-right column below zoom buttons on mobile */
  .audio-player {
    top: 104px;
    right: 12px;
    width: auto; height: auto;
    background: transparent;
    border: none;
    backdrop-filter: none;
    gap: 0;
  }
  /* Button inside: styled like other right-column buttons */
  .audio-player .audio-btn {
    width: 36px; height: 36px;
    border-radius: 8px;
    background: rgba(0,0,0,0.6);
    border: 1px solid rgba(255,255,255,0.15);
    backdrop-filter: blur(8px);
    transition: background 0.15s, box-shadow 0.2s;
  }
  .audio-player.audio-playing .audio-btn {
    background: rgba(59,130,246,0.75);
    border-color: rgba(59,130,246,0.5);
    box-shadow: 0 0 0 0 rgba(59,130,246,0.6);
    animation: audio-pulse 2s ease-out infinite;
  }
  @keyframes audio-pulse {
    0%   { box-shadow: 0 0 0 0   rgba(59,130,246,0.6); }
    70%  { box-shadow: 0 0 0 8px rgba(59,130,246,0); }
    100% { box-shadow: 0 0 0 0   rgba(59,130,246,0); }
  }

  .compass-wrapper {
    top: 12px;
    left: 12px;
  }

  .template-menu {
    bottom: 120px;
    right: 12px;
    left: 12px;
    min-width: auto;
  }
}

/* Very small screens (phones in portrait) */
@media (max-width: 480px) {
  .controls-container {
    padding: 3px;
    gap: 1px;
    border-radius: 8px !important;
  }

  .controls-container button {
    width: 30px;
    height: 30px;
  }

  .controls-container button svg {
    width: 14px;
    height: 14px;
  }

  /* Adjust audio icon position — zoom buttons are 30px each at 480px */
  .audio-player {
    top: 92px;
  }
  .audio-player .audio-btn {
    width: 30px;
    height: 30px;
  }
}

/* Free plan watermark — top-left, right of compass */
.free-watermark {
  position: absolute;
  top: 14px;
  left: 72px;   /* compass: 16px left + 48px width + 8px gap */
  bottom: auto;
  right: auto;
  z-index: 20;
  pointer-events: auto;
}
.free-watermark a {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  border-radius: 6px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  transition: background 0.2s, color 0.2s;
}
.free-watermark a:hover {
  background: rgba(0, 0, 0, 0.6);
  color: rgba(255, 255, 255, 0.95);
}
@media (max-width: 768px) {
  /* Hide powered-by on mobile — already shown on loading screen */
  .free-watermark {
    display: none;
  }
}
</style>
